import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import { FlightCard, SearchResponse } from '@/src/types/flight'
import { isEmpty } from '../../generalUtils'
import { getSplitId } from '../searchUtils/getSplitId'
import { constructSplrt } from './constructSplRT'

const debugMode = false
export const modifyPricesAndSortOnwardLeg = (flightResultResponseData: SearchResponse) => {
    if (debugMode) {
        console.log('modifyPricesAndSortOnwardLeg debug mode is enabled')
    }
    if (flightResultResponseData?.cards?.length < 2) {
        return flightResultResponseData
    }

    const { cards = [], sortData } = flightResultResponseData || {}
    const cheapestReturnCardId = sortData?.price_up[1]?.[0] || ''
    const cheapestReturnCardIndex = Number(getSplitId(cheapestReturnCardId)?.[1]) || 0
    const cheapestReturnCardPrice = cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.pr || 0
    const cheapestReturnCardPriceWODiscount =
        (cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.pr || 0) +
        (cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.ds || 0)
    if (Array.isArray(cards[0])) {
        cards[0].forEach((onwardCard, index) => {
            let cheapestPriceForCard = onwardCard.priceBreakup.pr + cheapestReturnCardPrice
            let cheapestPriceForCardWODiscount =
                onwardCard.priceBreakup.pr + onwardCard.priceBreakup.ds + cheapestReturnCardPriceWODiscount
            let cheapestPriceForSpecialRT = cheapestPriceForCard
            let cheapestPriceForSpecialRTWODiscount = cheapestPriceForCardWODiscount

            // Track which return card is used for this onward card (debug code)
            let usedReturnCardId = cheapestReturnCardId // Default to cheapest return card
            let usedReturnCardType = 'cheapest' // Track if using cheapest or special RT
            // End of debug code

            let selectedOnwardSplrtFare = 0
            let selectedReturnSplrtFare = 0
            // Regular retail `pr` of the special-partner return — used to build the RT retail strike-off.
            let selectedReturnRegularPr = 0

            cards[1].forEach((returnCard, returnIndex) => {
                const {
                    isSpecialRT: isOnwawrdSpecialRT,
                    splrtFare: onwardSplrtFare,
                    splrtFareWODiscount: onwardSplrtFareWODiscount
                } = constructSplrt(onwardCard, returnCard.splRtFn) || {}
                const {
                    isSpecialRT: isReturnSpecialRT,
                    splrtFare: returnSplrtFare,
                    splrtFareWODiscount: returnSplrtFareWODiscount
                } = constructSplrt(returnCard, onwardCard.splRtFn) || {}
                const specialRTPrice = (onwardSplrtFare || 0) + (returnSplrtFare || 0)
                const specialRTPriceWODiscount = (onwardSplrtFareWODiscount || 0) + (returnSplrtFareWODiscount || 0)
                if (
                    isOnwawrdSpecialRT &&
                    isReturnSpecialRT &&
                    specialRTPrice < cheapestPriceForSpecialRT &&
                    specialRTPrice < cheapestPriceForCard
                ) {
                    cheapestPriceForSpecialRT = specialRTPrice
                    cheapestPriceForSpecialRTWODiscount = specialRTPriceWODiscount
                    //debug code
                    usedReturnCardId = returnCard.id // Update to special RT return card
                    // End of debug code

                    selectedOnwardSplrtFare = onwardSplrtFare || 0
                    selectedReturnSplrtFare = returnSplrtFare || 0
                    selectedReturnRegularPr = returnCard.priceBreakup.pr || 0
                }
            })

            if (cheapestPriceForSpecialRT < cheapestPriceForCard) {
                usedReturnCardType = 'specialRT'
                onwardCard.priceBreakup.isMultiAirlinePriceSpecialRT = true
                onwardCard.priceBreakup.selectedOnwardSplrtFare = selectedOnwardSplrtFare
                onwardCard.priceBreakup.selectedReturnSplrtFare = selectedReturnSplrtFare
                // Split view: the per-leg card shows plain RETAIL (pr). The special-RT saving lives only on
                // the combined total banner (multiAirlinePrice + multiAirlinePriceStrikeOff), so the leg fare
                // is never decomposed and never carries a per-leg strike.
                onwardCard.priceBreakup.splitLegFare = onwardCard.priceBreakup.pr
                onwardCard.priceBreakup.splitLegStrikeOffPrice = onwardCard?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice
                onwardCard.priceBreakup.multiAirlinePrice = cheapestPriceForSpecialRT
                onwardCard.priceBreakup.multiAirlinePriceWoDiscount =
                    cheapestPriceForSpecialRTWODiscount > cheapestPriceForSpecialRT
                        ? cheapestPriceForSpecialRTWODiscount
                        : undefined
                // RT retail strike = this onward's regular pr + the special-partner return's regular pr
                // (= sum of the two legs' individual retail strikes), shown struck against the special RT total.
                {
                    const onwardComboRetail = onwardCard.priceBreakup.pr + selectedReturnRegularPr
                    onwardCard.priceBreakup.multiAirlinePriceStrikeOff =
                        onwardComboRetail > cheapestPriceForSpecialRT ? onwardComboRetail : undefined
                }

                const couponDetail = onwardCard?.priceBreakup?.fare?.coupon_detail

                if (couponDetail?.discountAmount) {
                    couponDetail.discountedPrice = cheapestPriceForSpecialRT - couponDetail.discountAmount
                }
            } else {
                onwardCard.priceBreakup.multiAirlinePrice = cheapestPriceForCard
                // Split view: regular combo → onward shows its own retail fare, no strike-off.
                onwardCard.priceBreakup.splitLegFare = onwardCard.priceBreakup.pr
                onwardCard.priceBreakup.splitLegStrikeOffPrice = onwardCard?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice
                onwardCard.priceBreakup.multiAirlinePriceWoDiscount =
                    cheapestPriceForCardWODiscount > cheapestPriceForCard ? cheapestPriceForCardWODiscount : undefined
                // Regular combo (no special-RT saving) → no RT retail strike.
                onwardCard.priceBreakup.multiAirlinePriceStrikeOff = undefined

                const couponDetail = onwardCard?.priceBreakup?.fare?.coupon_detail

                if (couponDetail?.discountAmount) {
                    couponDetail.discountedPrice = cheapestPriceForCard - couponDetail.discountAmount
                }
            }

            // This return card(usedReturnCardId) ID is valid only for regular fares (non–fare-family or regular fare-family plans).
            // For other fare types, the return card will be determined dynamically.
            // This ID is not applicable to non-regular fares.

            onwardCard.usedRegularFareReturnCardId = usedReturnCardId

            // Console log which return card is used for this onward card
            if (debugMode) {
                console.log(
                    `🛫 Onward Card: ${onwardCard.id} | Return Card Used: ${usedReturnCardId} | Type: ${usedReturnCardType} | Final Price: ${onwardCard.priceBreakup.multiAirlinePrice}`
                )
            }
        })

        sortData.price_up[0] = cards[0]
            .toSorted((a, b) => {
                return (a.priceBreakup.multiAirlinePrice || 0) - (b.priceBreakup.multiAirlinePrice || 0)
            })
            .map(card => {
                return card.id
            })

        // Split-view "One-way price" sort: order onward cards by their INDIVIDUAL leg fare
        // (splitLegFare, finalized on every card above) instead of the combined RT total. Kept next to
        // price_up so the sort UI can toggle "Total price" ↔ "One-way price" with no recompute.
        if (!sortData.oneWayPrice_up) sortData.oneWayPrice_up = []
        sortData.oneWayPrice_up[0] = cards[0]
            .toSorted((a, b) => {
                return (a.priceBreakup.splitLegFare || 0) - (b.priceBreakup.splitLegFare || 0)
            })
            .map(card => {
                return card.id
            })

        if (debugMode) {
            console.log('after sorting onward cards (cheapest)', sortData.price_up[0])
        }
    }
    return flightResultResponseData
}

export const modifyPricesAndSortOfReturnLeg = (
    flightResultResponseData: SearchResponse,
    selectedOnwardCard: FlightCard,
    selectedOnwardFareFamilyPlan: Plan | null
) => {
    if (!isEmpty(selectedOnwardCard) && Array.isArray(flightResultResponseData.cards[1])) {
        if (debugMode) {
            console.log('modifyPricesAndSortOfReturnLeg debug mode is enabled')
        }
        flightResultResponseData.cards[1].forEach(returnCard => {
            if (selectedOnwardFareFamilyPlan && selectedOnwardFareFamilyPlan?.ctBrandName !== 'Regular') {
                const currentReturnCardPrice = returnCard?.priceBreakup?.pr
                //returnCardPriceForSelectedOnwardFareFamilyPlan is the price of the return card that is used to calculate the multiAirlinePrice for the selected onward fare family plan
                const returnCardPriceUsedForSelectedOnwardFareFamilyPlan =
                    selectedOnwardFareFamilyPlan?.price - (selectedOnwardFareFamilyPlan?.unModifiedTotalPrice || 0)

                returnCard.priceBreakup.multiAirlinePrice =
                    currentReturnCardPrice - returnCardPriceUsedForSelectedOnwardFareFamilyPlan || 0
                // Split view: fare-family onward → no special RT, return shows retail.
                returnCard.priceBreakup.splitLegFare = returnCard.priceBreakup.pr
                returnCard.priceBreakup.splitLegStrikeOffPrice = returnCard?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice
                // No special-RT deal under a fare-family onward → clear any combo-deal badge / RT strike.
                returnCard.priceBreakup.hasSpecialRTWithSelectedOnward = false
                returnCard.priceBreakup.specialRTComboTotal = undefined
                returnCard.priceBreakup.multiAirlinePriceStrikeOff = undefined
            } else {
                const { isSpecialRT: isOnwawrdSpecialRT, splrtFare: onwardSplrtFare } =
                    constructSplrt(selectedOnwardCard, returnCard.splRtFn) || {}
                const { isSpecialRT: isReturnSpecialRT, splrtFare: returnSplrtFare } =
                    constructSplrt(returnCard, selectedOnwardCard.splRtFn) || {}
                const normalPrice = returnCard.priceBreakup.pr + selectedOnwardCard.priceBreakup.pr
                let totalPrice = 0
                if (isOnwawrdSpecialRT && isReturnSpecialRT) {
                    const specialRTPrice = (onwardSplrtFare || 0) + (returnSplrtFare || 0)
                    const isNormalPrCheaper = normalPrice < specialRTPrice
                    totalPrice = isNormalPrCheaper ? normalPrice : specialRTPrice
                    const returnUsesSpecial = !isNormalPrCheaper
                    // Split view: per-leg card shows plain RETAIL (pr), onward-independent. The special-RT
                    // saving is surfaced only on the combined total banner below (multiAirlinePriceStrikeOff).
                    returnCard.priceBreakup.splitLegFare = returnCard.priceBreakup.pr
                    returnCard.priceBreakup.splitLegStrikeOffPrice = returnCard?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice
                    // Combo-deal badge: this return pairs with the selected onward for a special round-trip
                    // total that beats the plain sum. Expose that combined total (= totalPrice, the special sum here).
                    returnCard.priceBreakup.hasSpecialRTWithSelectedOnward = returnUsesSpecial
                    returnCard.priceBreakup.specialRTComboTotal = returnUsesSpecial ? totalPrice : undefined
                    // RT retail strike = the plain pr sum (selected onward + this return), struck against the
                    // special RT total. Equals the sum of the two legs' individual retail strikes.
                    returnCard.priceBreakup.multiAirlinePriceStrikeOff =
                        returnUsesSpecial && normalPrice > totalPrice ? normalPrice : undefined
                    if (debugMode) {
                        console.group(
                            `return card : ${returnCard?.id} | totalPrice(${isNormalPrCheaper ? 'OW_pr+RT_pr' : 'OW_splrt+RT_splrt'}): ${totalPrice} | isSpecialRTPrice: ${!isNormalPrCheaper} | OW_pr+RT_pr: ${normalPrice} | OW_splrt+RT_splrt: ${specialRTPrice}`
                        )
                    }
                } else {
                    totalPrice = normalPrice
                    // Split view: no special RT for this pair → return shows its retail fare.
                    returnCard.priceBreakup.splitLegFare = returnCard.priceBreakup.pr
                    returnCard.priceBreakup.splitLegStrikeOffPrice = returnCard?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice
                    returnCard.priceBreakup.hasSpecialRTWithSelectedOnward = false
                    returnCard.priceBreakup.specialRTComboTotal = undefined
                    returnCard.priceBreakup.multiAirlinePriceStrikeOff = undefined
                    if (debugMode) {
                        console.group(
                            `return card : ${returnCard?.id} | totalPrice(OW_pr+RT_pr): ${totalPrice} | isSpecialRTPrice: ${false} | OW_pr+RT_pr: ${normalPrice} | OW_splrt+RT_splrt: ${undefined}`
                        )
                    }
                }

                returnCard.priceBreakup.multiAirlinePrice =
                    totalPrice - (selectedOnwardCard.priceBreakup.multiAirlinePrice || 0)

                if (debugMode) {
                    console.log(
                        `return card : ${returnCard?.id} | finalPrice: ${returnCard.priceBreakup.multiAirlinePrice} | selectedOnwardCard.priceBreakup.multiAirlinePrice: ${selectedOnwardCard.priceBreakup.multiAirlinePrice}`
                    )
                    console.groupEnd()
                }
            }
        })

        flightResultResponseData.sortData.price_up[1] = flightResultResponseData.cards[1]
            .toSorted((a, b) => {
                return (a.priceBreakup.multiAirlinePrice || 0) - (b.priceBreakup.multiAirlinePrice || 0)
            })
            .map(card => {
                return card.id
            })

        // Split-view "One-way price" sort: order return cards by their INDIVIDUAL leg fare
        // (splitLegFare, set per card above vs the selected onward) instead of the combined delta.
        if (!flightResultResponseData.sortData.oneWayPrice_up) flightResultResponseData.sortData.oneWayPrice_up = []
        flightResultResponseData.sortData.oneWayPrice_up[1] = flightResultResponseData.cards[1]
            .toSorted((a, b) => {
                return (a.priceBreakup.splitLegFare || 0) - (b.priceBreakup.splitLegFare || 0)
            })
            .map(card => {
                return card.id
            })

        if (debugMode) {
            console.log('after sorting return cards (cheapest)', flightResultResponseData.sortData.price_up[1])
        }
    }

    return flightResultResponseData
}

// export const getModifySortedReturnLegCardIds = (cards: any[], selectedJourneyLeg, isIntlRT) => {
//     if (isIntlRT && selectedJourneyLeg === 1 && Array.isArray(cards)) {
//         return cards
//             .toSorted((a, b) => a.priceBreakup.multiAirlinePrice - b.priceBreakup.multiAirlinePrice)
//             .map(card => card.id)
//     }

//     return null
// }
